import React, { useMemo, useState, useEffect } from "react";
import { Breadcrumb, Select, Spin } from "antd";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import "./PhotoGallaryDetails.css";
import axiosInstance from "../services/axios";
import Heading from "../components/Heading";
import EmptyState from '../components/EmptyState';

const { Option } = Select;

const PhotoGallaryDetails = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  
  const availableYears = useMemo(
    () => ["All Years", "2023-2024", "2024-2025", "2025-2026", "2026-2027"],
    []
  );
  const [selectedYear, setSelectedYear] = useState(availableYears[0]);

  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';

    axiosInstance
      .get('/gallery/', { params: {  language: languageParam } })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) data = data.results || data.data || Object.values(data) || [];

        const mapped = (data || []).map((it, idx) => ({
          id: it.id || `g-${idx}`,
          src: it.image || it.imageUrl || it.image_path || '',
          alt: it.title || it.title || `gallery-${it.id || idx}`,
          date: it.created_at || it.date || new Date().toISOString(),
          span: 3, // default span, keep grid compact; can be enhanced later
        }));

        setGallery(mapped);
      })
      .catch((err) => {
        console.error('Failed to fetch gallery:', err);
        setGallery([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [i18n.language]);

  // Helper function to check if a date falls within a financial year
  const isDateInFinancialYear = (dateString, financialYear) => {
    const date = new Date(dateString);
    const [startYear, endYear] = financialYear.split('-').map(Number);
    
    // Financial year runs from April 1st to March 31st
    const fyStart = new Date(startYear, 3, 1); // April 1st (month is 0-indexed)
    const fyEnd = new Date(endYear, 2, 31); // March 31st
    
    return date >= fyStart && date <= fyEnd;
  };

  // Filter images based on selected financial year
  const images = useMemo(() => {
    if (selectedYear === "All Years") return gallery;
    return gallery.filter(img => isDateInFinancialYear(img.date || img.created_at, selectedYear));
  }, [selectedYear, gallery]);

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  return (
    <div className="photo-gallery-page section-padding bg-image-section">
      <div className="top-row">
        <div className="breadcrumb-title">
          <Breadcrumb separator=">" className="custom-breadcrumb" aria-label="breadcrumb">
            <Breadcrumb.Item>
              <span 
                onClick={() => handleBreadcrumbClick('/')} 
                style={{ cursor: 'pointer', color: '#3b4a3b' }}
              >
                {i18n.language === 'mr' ? 'मुख्यपृष्ठ' : 'Home'}
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{i18n.language === 'mr' ? 'फोटो गॅलरी' : 'Photo Gallery'}</Breadcrumb.Item>
          </Breadcrumb>

          <Heading content={i18n.language === 'mr' ? 'फोटो गॅलरी' : 'Photo Gallery'} />
        </div>

        <div className="filter-area">
          <Select
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            style={{ minWidth: 250 }}
            aria-label="Financial Year"
          >
            {availableYears.map((y) => {
              if (y === "All Years") {
                return (
                  <Option key={y} value={y}>
                    All Photos ({gallery.length} photos)
                  </Option>
                );
              }
              const imageCount = gallery.filter(img => isDateInFinancialYear(img.date || img.created_at, y)).length;
              return (
                <Option key={y} value={y}>
                  Financial Year {y} ({imageCount} {imageCount === 1 ? 'photo' : 'photos'})
                </Option>
              );
            })}
          </Select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Filter Results Info */}
          <div className="filter-results-info">
            <p style={{ 
              color: '#666', 
              fontSize: '14px', 
              margin: '10px 0',
              fontStyle: 'italic'
            }}>
              {selectedYear === "All Years" 
                ? `Showing all ${images.length} photos` 
                : `Showing ${images.length} ${images.length === 1 ? 'photo' : 'photos'} from ${selectedYear}`
              }
            </p>
          </div>

          {images.length === 0 ? (
            <div style={{ padding: '50px 0' }}>
              <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
            </div>
          ) : (
            <section className="gallery-grid-details" aria-label="Photo gallery">
              {images.map((img) => {
                const gridStyles = {
                  gridColumn: `span ${img.span}`,
                  ...(img.rowSpan ? { gridRow: `span ${img.rowSpan}` } : {}),
                };
                return (
                  <figure
                    key={img.id}
                    className="gallery-item"
                    style={gridStyles}
                    tabIndex={0}
                    aria-labelledby={`img-${img.id}-caption`}
                  >
                    <img loading="lazy" src={img.src} alt={img.alt} />
                    <figcaption id={`img-${img.id}-caption`} className="visually-hidden">
                      {img.alt}
                    </figcaption>
                  </figure>
                );
              })}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default PhotoGallaryDetails;